import { useQueryClient } from '@tanstack/react-query';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { login as loginRequest, fetchProfile } from '@/features/auth/api';
import type { AuthUser, LoginCredentials } from '@/features/auth/types';
import { isAdminRole } from '@/features/auth/types';
import {
  clearToken,
  getToken,
  isExpired,
  millisUntilExpiry,
  setToken,
  setUnauthorizedHandler,
} from '@/lib/api/session';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  readonly status: SessionStatus;
  readonly user: AuthUser | null;
  readonly isAdmin: boolean;
  /** True while the user must change their password before doing anything else. */
  readonly mustChangePassword: boolean;
  readonly signIn: (credentials: LoginCredentials) => Promise<void>;
  readonly signOut: (reason?: SignOutReason) => void;
  /** Called after a successful forced password change to lift the gate. */
  readonly clearPasswordGate: () => void;
  /** Non-null once the session is close enough to expiry to warn about. */
  readonly expiresInMs: number | null;
  readonly signOutReason: SignOutReason | null;
}

export type SignOutReason = 'expired' | 'revoked' | 'manual';

const AuthContext = createContext<AuthContextValue | null>(null);

/** Warn this long before the 60-minute token dies. There is no refresh endpoint. */
const EXPIRY_WARNING_MS = 5 * 60_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<SessionStatus>(() =>
    getToken() !== null && !isExpired() ? 'loading' : 'anonymous',
  );
  const [signOutReason, setSignOutReason] = useState<SignOutReason | null>(null);
  const [expiresInMs, setExpiresInMs] = useState<number | null>(null);

  const signOut = useCallback(
    (reason: SignOutReason = 'manual') => {
      // There is no logout endpoint — tokens are stateless. Signing out is local.
      clearToken();
      setUser(null);
      setStatus('anonymous');
      setSignOutReason(reason);
      setExpiresInMs(null);
      queryClient.clear();
    },
    [queryClient],
  );

  // The server rejected a token we sent: the account was deactivated, or the token died.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSignOutReason('revoked');
      clearToken();
      setUser(null);
      setStatus('anonymous');
      setExpiresInMs(null);
      queryClient.clear();
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);

  // Restore a session on load. The legacy app trusted localStorage blindly; this
  // confirms the token is still good before showing the app.
  useEffect(() => {
    const token = getToken();
    if (token === null || isExpired(token)) {
      if (token !== null) clearToken();
      setStatus('anonymous');
      return;
    }

    const controller = new AbortController();
    void fetchProfile(controller.signal)
      .then((profile) => {
        setUser(profile);
        setStatus('authenticated');
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        // The unauthorized handler already cleared a dead session; anything else
        // (offline, 500) should not silently strand the user in "loading".
        clearToken();
        setUser(null);
        setStatus('anonymous');
      });

    return () => controller.abort();
  }, []);

  // Expiry countdown. Warns first, then signs out exactly when the token dies —
  // rather than letting the next request fail and reloading the page, which is what
  // the legacy app did, destroying whatever the user had typed.
  useEffect(() => {
    if (status !== 'authenticated') return;

    const tick = (): void => {
      const remaining = millisUntilExpiry();
      if (remaining === null) return;
      if (remaining <= 0) {
        signOut('expired');
        return;
      }
      setExpiresInMs(remaining <= EXPIRY_WARNING_MS ? remaining : null);
    };

    tick();
    // A local expiry clock, not an API poll: there is no request for TanStack Query
    // to own here, so the polling rule does not apply.
    // eslint-disable-next-line no-restricted-syntax
    const timer = setInterval(tick, 30_000);
    return () => clearInterval(timer);
  }, [status, signOut]);

  const signIn = useCallback(async (credentials: LoginCredentials) => {
    const result = await loginRequest(credentials);
    setToken(result.token);
    setUser(result.user);
    setSignOutReason(null);
    setStatus('authenticated');
  }, []);

  const clearPasswordGate = useCallback(() => {
    setUser((current) => (current === null ? null : { ...current, mustChangePassword: false }));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isAdmin: user !== null && isAdminRole(user.role),
      mustChangePassword: user?.mustChangePassword === true,
      signIn,
      signOut,
      clearPasswordGate,
      expiresInMs,
      signOutReason,
    }),
    [status, user, signIn, signOut, clearPasswordGate, expiresInMs, signOutReason],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.');
  return context;
}
